import { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import {
  Search, CheckCircle2, XCircle, Activity,
  TrendingUp, AlertTriangle, Lock, ChevronDown, ChevronUp,
} from "lucide-react";

/**
 * Embeddable view for inspection results + live SPC analysis.
 * Used by the Inspection Results page (full screen) and the dashboard
 * "View Inspections" modal (compact = true).
 *
 * Props:
 *   initialPartNo?:  string  — pre-select this part on mount
 *   compact?:        boolean — denser layout for modal
 */
const InspectionResultsView = ({ initialPartNo = null, compact = false }) => {
  const [parts, setParts] = useState([]);
  const [filter, setFilter] = useState("");
  const [partNo, setPartNo] = useState(initialPartNo);
  const [partMeta, setPartMeta] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [activeDim, setActiveDim] = useState(null);
  const [spc, setSpc] = useState(null);
  const [loadingSpc, setLoadingSpc] = useState(false);
  const [loadingPart, setLoadingPart] = useState(false);

  // Load all inspected parts (sidebar / picker)
  useEffect(() => {
    api.get("/inspection/parts").then((r) => setParts(r.data || [])).catch(() => setParts([]));
  }, []);

  // When part changes, fetch recent inspections + dimension counts
  const loadPart = useCallback(async (pno) => {
    if (!pno) return;
    setLoadingPart(true);
    setActiveDim(null);
    setSpc(null);
    try {
      const [hist, dims] = await Promise.all([
        api.get(`/inspection/history?part_no=${encodeURIComponent(pno)}&limit=50`),
        api.get(`/inspection/dimensions?part_no=${encodeURIComponent(pno)}`),
      ]);
      setInspections(hist.data || []);
      setDimensions(dims.data || []);
      const meta = parts.find((p) => p.part_no === pno);
      setPartMeta(meta || { part_no: pno, part_name: hist.data?.[0]?.part_name || "" });
    } catch {
      setInspections([]); setDimensions([]);
    }
    setLoadingPart(false);
  }, [parts]);

  useEffect(() => { if (partNo) loadPart(partNo); }, [partNo, loadPart]);

  const loadSpc = async (dim) => {
    setActiveDim(dim);
    setLoadingSpc(true);
    setSpc(null);
    try {
      const r = await api.get(
        `/inspection/spc?part_no=${encodeURIComponent(partNo)}&dimension_description=${encodeURIComponent(dim.dimension_description)}`
      );
      setSpc(r.data);
    } catch {
      setSpc({ ready: false, n: 0, message: "Failed to load SPC data" });
    }
    setLoadingSpc(false);
  };

  const filteredParts = filter
    ? parts.filter((p) =>
        (p.part_no || "").toLowerCase().includes(filter.toLowerCase()) ||
        (p.part_name || "").toLowerCase().includes(filter.toLowerCase()))
    : parts;

  return (
    <div className={`grid ${compact ? "grid-cols-1 lg:grid-cols-3" : "grid-cols-1 lg:grid-cols-4"} gap-4`}>
      {/* Parts picker — hidden in compact when a part is preselected */}
      {!(compact && initialPartNo) && (
        <div className={compact ? "lg:col-span-1" : "lg:col-span-1"}>
          <div className="bg-white rounded-2xl border border-slate-200 p-3" data-testid="inspection-parts-picker">
            <div className="relative mb-2">
              <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <input
                data-testid="parts-filter-input"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter parts…"
                className="w-full pl-8 pr-2 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A3560]/30"
              />
            </div>
            <div className={`overflow-y-auto ${compact ? "max-h-72" : "max-h-[28rem]"} divide-y divide-slate-100`}>
              {filteredParts.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-6">No inspected parts</p>
              )}
              {filteredParts.map((p) => (
                <button
                  key={p.part_no}
                  data-testid={`part-row-${p.part_no}`}
                  onClick={() => setPartNo(p.part_no)}
                  className={`w-full text-left px-2 py-2 hover:bg-slate-50 rounded ${
                    partNo === p.part_no ? "bg-[#1A3560]/10" : ""
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-xs text-slate-900 truncate">{p.part_name || p.part_no}</p>
                      <p className="text-[10px] text-slate-500 font-mono truncate">{p.part_no}</p>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 shrink-0">
                      <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-bold">
                        {p.inspection_count}
                      </span>
                      {p.fail_count > 0 && (
                        <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">
                          {p.fail_count} FAIL
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Detail panel */}
      <div className={compact ? "lg:col-span-2" : "lg:col-span-3"}>
        {!partNo && (
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center">
            <Activity size={32} className="mx-auto text-slate-300 mb-2" />
            <p className="text-slate-400 text-sm">Select a part to view inspection results & SPC analysis</p>
          </div>
        )}

        {partNo && (
          <div className="space-y-3">
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-widest font-bold">Part</p>
                  <p className="font-extrabold text-slate-900 text-lg">{partMeta?.part_name || "—"}</p>
                  <p className="text-xs text-slate-500 font-mono">{partNo}</p>
                </div>
                <div className="flex gap-3 flex-wrap">
                  <Stat label="Inspections" value={partMeta?.inspection_count ?? inspections.length} />
                  <Stat label="Recent Pass" value={inspections.filter((i) => i.overall_result === "PASS").length} color="text-emerald-600" />
                  <Stat label="Recent Fail" value={inspections.filter((i) => i.overall_result === "FAIL").length} color="text-red-600" />
                </div>
              </div>
            </div>

            {/* Dimensions / SPC selector */}
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <p className="font-extrabold text-slate-900 text-sm mb-2">Dimensions ({dimensions.length})</p>
              <p className="text-xs text-slate-400 mb-3">Click a measured dimension to run live 3σ SPC. Unlocks at 100 readings.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                {loadingPart && <p className="text-xs text-slate-400">Loading…</p>}
                {!loadingPart && dimensions.length === 0 && (
                  <p className="text-xs text-slate-400 col-span-2">No measured readings yet for this part.</p>
                )}
                {dimensions.map((d) => {
                  const pct = Math.min(100, Math.round((d.reading_count / 100) * 100));
                  return (
                    <button
                      key={d.dimension_description}
                      data-testid={`dim-row-${d.dimension_description}`}
                      onClick={() => loadSpc(d)}
                      className={`text-left p-3 rounded-xl border-2 hover:border-[#1A3560] transition-all ${
                        activeDim?.dimension_description === d.dimension_description
                          ? "border-[#1A3560] bg-[#1A3560]/5"
                          : "border-slate-200"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-xs text-slate-900 truncate">{d.dimension_description}</p>
                          <p className="text-[10px] text-slate-500 mt-0.5">
                            LSL {d.lsl ?? "—"} / USL {d.usl ?? "—"}
                            {d.instrument && <> · {d.instrument}</>}
                          </p>
                        </div>
                        {d.spc_ready ? (
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold flex items-center gap-1">
                            <TrendingUp size={10} /> SPC
                          </span>
                        ) : (
                          <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-bold flex items-center gap-1">
                            <Lock size={10} /> {d.reading_count}/100
                          </span>
                        )}
                      </div>
                      <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${d.spc_ready ? "bg-emerald-500" : "bg-amber-400"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* SPC chart */}
            {activeDim && (
              <SpcPanel dim={activeDim} spc={spc} loading={loadingSpc} />
            )}

            {/* Recent inspections list */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                <p className="font-extrabold text-slate-900 text-sm">Recent Inspections</p>
                <span className="text-xs text-slate-400">click any row to view readings · last {Math.min(50, inspections.length)}</span>
              </div>
              <div className="max-h-[28rem] overflow-y-auto">
                {inspections.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-6">No inspections yet</p>
                )}
                {inspections.map((i) => (
                  <InspectionRow key={i.inspection_id} ins={i} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const InspectionRow = ({ ins }) => {
  const [open, setOpen] = useState(false);
  const failCount = (ins.dimensions || []).filter((d) => d.result === "FAIL").length;
  const totalDims = (ins.dimensions || []).length;
  return (
    <div className="border-b border-slate-100" data-testid={`inspection-row-${ins.inspection_id}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors"
      >
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] text-slate-400">{ins.date} {ins.time}</p>
          <p className="font-bold text-slate-700 text-[11px]">
            {ins.tool_no} · {ins.machine_code}
            <span className="ml-2 text-slate-400 font-normal">{ins.inspection_type} · {ins.sample_size} samples · {ins.inspector_name}</span>
          </p>
        </div>
        {ins.overall_result === "PASS" ? (
          <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-bold">
            <CheckCircle2 size={10} /> PASS
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded font-bold">
            <XCircle size={10} /> {failCount}/{totalDims} FAIL
          </span>
        )}
        {open ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
      </button>
      {open && (
        <div className="bg-slate-50 border-t border-slate-200 p-3" data-testid={`inspection-detail-${ins.inspection_id}`}>
          {ins.overall_remarks && (
            <p className="text-[11px] text-slate-600 mb-2 italic">"{ins.overall_remarks}"</p>
          )}
          <table className="w-full text-[11px] bg-white rounded-lg overflow-hidden">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                <th className="px-2 py-1.5 text-left font-bold uppercase tracking-wider text-[9px]">#</th>
                <th className="px-2 py-1.5 text-left font-bold uppercase tracking-wider text-[9px]">Dimension</th>
                <th className="px-2 py-1.5 text-left font-bold uppercase tracking-wider text-[9px]">Type / Inst.</th>
                <th className="px-2 py-1.5 text-left font-bold uppercase tracking-wider text-[9px]">Spec</th>
                <th className="px-2 py-1.5 text-left font-bold uppercase tracking-wider text-[9px]">Readings</th>
                <th className="px-2 py-1.5 text-center font-bold uppercase tracking-wider text-[9px]">Result</th>
              </tr>
            </thead>
            <tbody>
              {(ins.dimensions || []).map((d, idx) => (
                <tr key={idx} className="border-t border-slate-100">
                  <td className="px-2 py-1.5 text-slate-400 font-mono">{d.s_no || idx + 1}</td>
                  <td className="px-2 py-1.5 font-bold text-slate-800">
                    {d.dimension_description}
                    {d.remarks && <p className="text-slate-400 font-normal italic text-[10px]">{d.remarks}</p>}
                  </td>
                  <td className="px-2 py-1.5 text-slate-500">
                    {d.check_type}
                    {d.instrument && <p className="text-slate-400 text-[10px]">{d.instrument}</p>}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600 font-mono text-[10px]">
                    {d.lsl != null && d.usl != null
                      ? `${d.lsl} – ${d.usl}`
                      : d.lsl != null ? `≥ ${d.lsl}` : d.usl != null ? `≤ ${d.usl}` : "—"}
                  </td>
                  <td className="px-2 py-1.5">
                    {d.check_type === "Measured" ? (
                      <ReadingsRow readings={d.readings} lsl={d.lsl} usl={d.usl} />
                    ) : (
                      <span className="text-slate-600 font-bold text-[10px]">
                        {d.toggle_result || (d.result === "PASS" ? "OK" : "NOT OK")}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    {d.result === "PASS" ? (
                      <span className="inline-block text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold">PASS</span>
                    ) : (
                      <span className="inline-block text-[9px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">FAIL</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ReadingsRow = ({ readings, lsl, usl }) => {
  if (!readings || readings.length === 0) return <span className="text-slate-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {readings.map((r, i) => {
        const v = typeof r === "object" ? (r.value ?? r.reading ?? r.v) : r;
        const num = typeof v === "number" ? v : parseFloat(v);
        const oos = !isNaN(num) && ((lsl != null && num < lsl) || (usl != null && num > usl));
        return (
          <span
            key={i}
            className={`px-1.5 py-0.5 rounded font-mono text-[10px] font-bold ${
              oos ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"
            }`}
            title={typeof r === "object" && r.cavity ? `Cavity ${r.cavity}` : ""}
          >
            {typeof r === "object" && r.cavity ? `C${r.cavity}: ` : ""}{v}
          </span>
        );
      })}
    </div>
  );
};

const Stat = ({ label, value, color = "text-slate-900" }) => (
  <div className="text-center">
    <p className={`text-2xl font-extrabold ${color} leading-none`}>{value}</p>
    <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold mt-1">{label}</p>
  </div>
);

const SpcPanel = ({ dim, spc, loading }) => {
  if (loading || !spc) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
        <div className="w-6 h-6 border-2 border-[#1A3560] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
        <p className="text-xs text-slate-400">Computing SPC for "{dim.dimension_description}"…</p>
      </div>
    );
  }

  if (!spc.ready) {
    const pct = Math.min(100, Math.round((spc.n / 100) * 100));
    return (
      <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5" data-testid="spc-locked">
        <div className="flex items-start gap-3">
          <Lock size={20} className="text-amber-700 mt-0.5" />
          <div className="flex-1">
            <p className="font-extrabold text-amber-900 text-sm">SPC analysis locked</p>
            <p className="text-xs text-amber-800 mt-1">{spc.message}</p>
            <p className="text-xs text-amber-700 mt-2 font-bold">{spc.n} / 100 readings</p>
            <div className="mt-2 h-2 bg-amber-100 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const s = spc.stats;
  const verdictColor = {
    Excellent: "bg-emerald-100 text-emerald-800 border-emerald-300",
    Capable: "bg-blue-100 text-blue-800 border-blue-300",
    Marginal: "bg-amber-100 text-amber-800 border-amber-300",
    "Not Capable": "bg-red-100 text-red-800 border-red-300",
    "—": "bg-slate-100 text-slate-700 border-slate-300",
  }[s.verdict] || "bg-slate-100 text-slate-700 border-slate-300";

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="spc-ready">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">SPC — 3σ Control Chart</p>
          <p className="font-extrabold text-slate-900">{dim.dimension_description}</p>
          <p className="text-xs text-slate-500">{spc.n} readings · LSL {spc.lsl ?? "—"} / USL {spc.usl ?? "—"}</p>
        </div>
        {s.cpk !== null && (
          <span className={`text-xs px-3 py-1.5 rounded-xl border font-bold ${verdictColor}`}>
            Cpk {s.cpk} · {s.verdict}
          </span>
        )}
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 bg-slate-50 rounded-xl p-3">
        <StatTiny label="X̄ (mean)" value={s.mean} />
        <StatTiny label="σ" value={s.sigma} />
        <StatTiny label="UCL" value={s.ucl} color="text-blue-700" />
        <StatTiny label="LCL" value={s.lcl} color="text-blue-700" />
        <StatTiny label="Cp" value={s.cp ?? "—"} />
        <StatTiny label="Cpk" value={s.cpk ?? "—"} />
      </div>

      {/* Chart */}
      <SpcChart spc={spc} />

      {(s.out_of_control > 0 || s.out_of_spec > 0) && (
        <div className="flex gap-3 flex-wrap text-xs">
          {s.out_of_control > 0 && (
            <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 px-3 py-1.5 rounded-lg font-bold border border-red-200">
              <AlertTriangle size={12} /> {s.out_of_control} reading{s.out_of_control > 1 ? "s" : ""} outside 3σ
            </span>
          )}
          {s.out_of_spec > 0 && (
            <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-800 px-3 py-1.5 rounded-lg font-bold border border-amber-200">
              <AlertTriangle size={12} /> {s.out_of_spec} reading{s.out_of_spec > 1 ? "s" : ""} outside spec
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const StatTiny = ({ label, value, color = "text-slate-900" }) => (
  <div className="text-center">
    <p className="text-[9px] text-slate-400 uppercase tracking-widest font-bold">{label}</p>
    <p className={`font-extrabold text-sm ${color} mt-0.5`}>{value}</p>
  </div>
);

/**
 * Pure-SVG SPC chart — no chart library needed.
 * Plots all readings in time-order with mean (green), UCL/LCL (blue dashed),
 * and LSL/USL (red dashed) reference lines.
 */
const SpcChart = ({ spc }) => {
  const points = spc.points;
  const s = spc.stats;
  const W = 720, H = 220, PADL = 56, PADR = 16, PADT = 16, PADB = 32;
  const innerW = W - PADL - PADR;
  const innerH = H - PADT - PADB;

  // Y range: extend to include all reference lines + readings
  const yMin = Math.min(s.lcl, ...(spc.lsl != null ? [spc.lsl] : []), ...points.map((p) => p.value));
  const yMax = Math.max(s.ucl, ...(spc.usl != null ? [spc.usl] : []), ...points.map((p) => p.value));
  const yPad = (yMax - yMin) * 0.08 || 0.1;
  const y0 = yMin - yPad, y1 = yMax + yPad;
  const yScale = (v) => PADT + innerH - ((v - y0) / (y1 - y0)) * innerH;
  const xScale = (i) => PADL + (i / Math.max(1, points.length - 1)) * innerW;

  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xScale(i).toFixed(1)} ${yScale(p.value).toFixed(1)}`)
    .join(" ");

  // Y-axis ticks (5)
  const ticks = Array.from({ length: 5 }, (_, i) => y0 + (i / 4) * (y1 - y0));

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet" className="bg-white rounded-lg" data-testid="spc-chart">
        {/* Grid */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={PADL} x2={W - PADR} y1={yScale(t)} y2={yScale(t)} stroke="#f1f5f9" />
            <text x={PADL - 6} y={yScale(t) + 3} fontSize="9" textAnchor="end" fill="#94a3b8">
              {t.toFixed(3)}
            </text>
          </g>
        ))}
        {/* Spec band */}
        {spc.lsl != null && spc.usl != null && (
          <rect
            x={PADL}
            y={yScale(spc.usl)}
            width={innerW}
            height={Math.max(0, yScale(spc.lsl) - yScale(spc.usl))}
            fill="#fef2f2"
            opacity="0.4"
          />
        )}
        {/* 3σ band */}
        <rect
          x={PADL}
          y={yScale(s.ucl)}
          width={innerW}
          height={Math.max(0, yScale(s.lcl) - yScale(s.ucl))}
          fill="#dbeafe"
          opacity="0.35"
        />
        {/* Reference lines */}
        <RefLine y={yScale(s.mean)} x1={PADL} x2={W - PADR} stroke="#15803D" label={`X̄ ${s.mean}`} />
        <RefLine y={yScale(s.ucl)} x1={PADL} x2={W - PADR} stroke="#1d4ed8" dashed label={`UCL ${s.ucl}`} />
        <RefLine y={yScale(s.lcl)} x1={PADL} x2={W - PADR} stroke="#1d4ed8" dashed label={`LCL ${s.lcl}`} />
        {spc.usl != null && <RefLine y={yScale(spc.usl)} x1={PADL} x2={W - PADR} stroke="#dc2626" dashed label={`USL ${spc.usl}`} />}
        {spc.lsl != null && <RefLine y={yScale(spc.lsl)} x1={PADL} x2={W - PADR} stroke="#dc2626" dashed label={`LSL ${spc.lsl}`} />}
        {/* Series */}
        <path d={path} fill="none" stroke="#1A3560" strokeWidth="1.5" />
        {points.map((p, i) => {
          const ooc = p.value > s.ucl || p.value < s.lcl;
          const oos = (spc.lsl != null && p.value < spc.lsl) || (spc.usl != null && p.value > spc.usl);
          const fill = oos ? "#dc2626" : ooc ? "#f59e0b" : "#1A3560";
          return (
            <circle
              key={i}
              cx={xScale(i)}
              cy={yScale(p.value)}
              r={oos || ooc ? 3 : 1.8}
              fill={fill}
            >
              <title>{`${p.date} ${p.time} · ${p.machine_code} · ${p.value}`}</title>
            </circle>
          );
        })}
        {/* X axis label */}
        <text x={PADL} y={H - 8} fontSize="9" fill="#94a3b8">first reading</text>
        <text x={W - PADR} y={H - 8} fontSize="9" fill="#94a3b8" textAnchor="end">latest ({points.length})</text>
      </svg>
    </div>
  );
};

const RefLine = ({ y, x1, x2, stroke, dashed, label }) => (
  <g>
    <line x1={x1} x2={x2} y1={y} y2={y} stroke={stroke} strokeWidth="1" strokeDasharray={dashed ? "4 3" : "0"} />
    <text x={x2 - 4} y={y - 3} fontSize="9" fill={stroke} textAnchor="end" fontWeight="bold">{label}</text>
  </g>
);

export default InspectionResultsView;
